import { useState } from 'preact/hooks'
import { websApi, type WebViewDto, type WebUpdateDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import { routes } from '@/utils/routes'
import { Trash2, X } from 'lucide-preact'

interface Props {
  isOpen: boolean
  onClose: () => void
  web: WebViewDto
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
}

export function DeleteWebModal({ isOpen, onClose, web, clientCompanyId, workspaceId, campaignId, onPatchWeb }: Props) {
  const { showError, showErrorUnless422, showSuccess } = useToast()
  const [deleting, setDeleting] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [deleteBigMarker, setDeleteBigMarker] = useState(false)

  const hasConference = !!web.OnlineEvent?.ConferenceId
  const hasLeads = (web.LeadCount || 0) > 0

  const handleClose = () => {
    if (!deleting && !archiving) {
      onClose()
      setDeleteBigMarker(false)
    }
  }

  const handleArchive = async () => {
    setArchiving(true)
    const includedIds = (web.IncludedFileImports || []).map(fi => fi.Id)
    const dto: WebUpdateDto = { Id: web.Id, WorkspaceId: web.WorkspaceId, CampaignId: web.CampaignId, Name: web.Name, Type: web.Type, Status: 'Archived', LaunchDate: web.LaunchDate, Description: web.Description, AssetId: web.AssetId, EntryRulesJson: web.EntryRulesJson, CompletionRulesJson: web.CompletionRulesJson, LeadGoal: web.LeadGoal, OnlineEvent: web.OnlineEvent ? { EventType: web.OnlineEvent.EventType, ModeType: web.OnlineEvent.ModeType, Title: web.OnlineEvent.Title, TimeZone: web.OnlineEvent.TimeZone, StartDateTime: web.OnlineEvent.StartDateTime, EndDateTime: web.OnlineEvent.EndDateTime, ConferenceId: web.OnlineEvent.ConferenceId, Overview: web.OnlineEvent.Overview, Agenda: web.OnlineEvent.Agenda, Speakers: web.OnlineEvent.Speakers } : undefined, IncludedFileImportIds: includedIds }
    const res = await websApi.updateWeb(web.Id, dto)
    if (!res.error && res.data) { onPatchWeb(() => res.data!); showSuccess('Web archived', 'Status updated to Archived.'); onClose() } else showError('Archive failed', res.error || 'Unable to archive web')
    setArchiving(false)
  }

  const handleConfirmDelete = async () => {
    setDeleting(true)
    const res = await websApi.deleteWeb(web.WorkspaceId, web.CampaignId, web.Id, deleteBigMarker)
    if (!res.error && res.status === 204) {
      showSuccess('Web deleted', 'The web has been removed.')
      const scopedRoutes = routes.scoped(clientCompanyId, workspaceId)
      const target = scopedRoutes.campaign(campaignId)
      try {
        history.pushState({}, '', target)
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch {
        window.history.pushState({}, '', target)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    } else { showErrorUnless422('Delete failed', res, 'Unable to delete web'); setDeleting(false) }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-md rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-5 space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> {hasLeads ? 'Archive web' : 'Delete web'}</h3>
        {hasLeads ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">This web already has <strong>{web.LeadCount.toLocaleString()}</strong> lead{web.LeadCount === 1 ? '' : 's'}. It can be archived instead.</p>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">Are you sure you want to permanently delete this web?</p>
        )}
        {hasConference && !hasLeads && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none">
              <input type="checkbox" checked={deleteBigMarker} onChange={(e) => setDeleteBigMarker((e.target as HTMLInputElement).checked)} className="rounded border-slate-300 dark:border-slate-600" />
              Also delete BigMarker conference
            </label>
            {deleteBigMarker && (
              <p className="text-xs text-red-600 dark:text-red-400">This will permanently delete the conference in BigMarker. This action cannot be undone.</p>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700" onClick={() => { onClose(); setDeleteBigMarker(false) }} disabled={deleting || archiving}><X className="w-4 h-4" /> Cancel</button>
          {hasLeads ? (
            <button type="button" onClick={handleArchive} disabled={archiving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-amber-500 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20">{archiving ? 'Archiving...' : 'Archive'}</button>
          ) : (
            <button type="button" onClick={handleConfirmDelete} disabled={deleting} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">{deleting ? 'Deleting...' : 'Delete'}</button>
          )}
        </div>
      </div>
    </div>
  )
}
